import { IframeUrlState } from './iframe-url-state';
import { decodeChainTabs, encodeChainTabs, withChainTabs } from './record-chain';

export interface HostLocation {
	pathname: string;
	search: string;
}

export interface HostUrlCodec {
	decode(location: HostLocation): IframeUrlState;
	encode(state: IframeUrlState, location: HostLocation): HostLocation;
}

export interface RecordPathCodecOptions {
	resolveBasePath(pathname: string): string | null;
	recordSegment: string;
}

const BRIDGE_QUERY_PARAMS = ['mode', 'tab', 'rtab', 'page'] as const;
const NESTED_RECORD_SEGMENT = 'records';

function parseTabParam(value: string | null): number | null {
	return value !== null && /^\d+$/.test(value) ? Number(value) : null;
}

function parseRecordPairs(segments: string[]): { rid: string; segment: string | null }[] {
	if (segments.length === 0 || segments.length % 2 !== 0) return [];
	try {
		const pairs: { rid: string; segment: string | null }[] = [];
		for (let i = 0; i < segments.length; i += 2) {
			pairs.push({ segment: decodeURIComponent(segments[i]), rid: decodeURIComponent(segments[i + 1]) });
		}
		return pairs;
	} catch {
		return [];
	}
}

export function createRecordPathCodec({ resolveBasePath, recordSegment }: RecordPathCodecOptions): HostUrlCodec {
	return {
		decode(location) {
			const base = resolveBasePath(location.pathname);

			const onSurface = base !== null && location.pathname.startsWith(base);
			const rids = onSurface
				? parseRecordPairs(
						location.pathname
							.slice(base.length)
							.split('/')
							.filter((segment) => segment.length > 0),
					)
				: [];

			const query = new URLSearchParams(location.search);
			const mode = query.get('mode');

			return {
				chain: withChainTabs(rids, decodeChainTabs(query.get('rtab'))),
				mode: mode === 'view' || mode === 'edit' ? mode : null,
				tab: parseTabParam(query.get('tab')),
				page: query.get('page'),
			};
		},

		encode(state, location) {
			const base = resolveBasePath(location.pathname) ?? location.pathname;
			const pathname = state.chain.reduce((path, entry, depth) => {
				const segment = entry.segment ?? (depth === 0 ? recordSegment : NESTED_RECORD_SEGMENT);
				return `${path}/${encodeURIComponent(segment)}/${encodeURIComponent(entry.rid)}`;
			}, base);

			const query = new URLSearchParams(location.search);
			BRIDGE_QUERY_PARAMS.forEach((param) => query.delete(param));
			if (state.mode !== null) query.set('mode', state.mode);
			if (state.tab !== null) query.set('tab', String(state.tab));
			if (state.page !== null) query.set('page', state.page);
			const rtab = encodeChainTabs(state.chain);
			if (rtab !== null) query.set('rtab', rtab);

			const search = query.toString();
			return { pathname, search: search.length > 0 ? `?${search}` : '' };
		},
	};
}
